import type { IArticleProps, ISimplifiedArticleProps, ITag, ThumbnailProps } from '@/interfaces';

import { getThumbnailsByTag, tagsAndMarketsQuery } from '@/fetchers';
import callContentful from './base.fetchers';

import { ArticleTypeName } from '@/constants';
import { articleDataModify, sortByDate } from '@/utils';

interface IGetArticleArgs {
  isPreviewMode: boolean;
  articleTypeName: string;
  slug: string;
}

export const getArticle = async (args: IGetArticleArgs): Promise<IArticleProps | null> => {
  const { isPreviewMode, articleTypeName, slug } = args;

  const query = `
    {
      articleCollection(
        preview: ${isPreviewMode}
        where: {
          articleType: "${articleTypeName}"
          slug: "${slug}"
        }
        limit: 1
      ) {
        items {
          sys {
            id
            publishedAt
          }
          title
          breadcrumbsTheme
          articleType
          slug
          date
          image {
            contentType
            url(transform: {
              width: 696,
            })
          }
          tags: tagsCollection(preview: ${isPreviewMode}) {
            items {
              ${tagsAndMarketsQuery()}
            }
          }
          content {
            json
            links {
              assets {
                block {
                  sys {
                    id
                  }
                  __typename
                  contentType
                  url
                  title
                  description
                  height
                  width
                }
              }
              entries {
                block {
                  sys {
                    id
                  }
                  __typename
                  ... on Button {
                    primaryButtonText
                    secondaryButtonText
                    function
                    color
                    email
                    buttonLink
                    openInNewTab
                    triggerPopUp
                  }
                  ... on LeadGenBanner {
                    title
                    titleMobile
                    image {
                      contentType
                      url(transform: {
                        width: 696,
                      })
                      description
                    }
                    imageMobile {
                      contentType
                      url(transform: {
                        width: 696,
                      })
                      description
                    }
                    subCopy
                    subCopyMobile
                    buttons: buttonsCollection(limit: 3) {
                      items {
                        sys {
                          id
                        }
                        primaryButtonText
                        secondaryButtonText
                        function
                        color
                        email
                        buttonLink
                        appStoreUrl
                        googlePlayUrl
                        buttonEventLabel
                        openInNewTab
                      }
                    }
                    sectionTheme: theme
                    isLinkedInBanner
                  }
                  ... on People {
                    image {
                      contentType
                      url(transform: {
                        width: 130,
                      })
                      description
                    }
                    fullName
                    subHeader
                    personDescription
                    profileLink
                  }
                }
              }
            }
          }
          seo {
            title
            description
            ogType
            ogUrl
            ogImage {
              contentType
              url
            }
            ogTitle
            ogDescription
            robots
          }
        }
      }
    }
  `;

  const response = await callContentful(
    query,
    isPreviewMode,
    `/${articleTypeName}/${slug}`,
    'article.fetchers.getArticle',
  );

  return response.data?.articleCollection?.items?.[0]
    ? articleDataModify(response.data?.articleCollection?.items?.[0])
    : null;
};

interface IGetRelatedArticleArgs {
  isPreviewMode: boolean;
  currentArticleTags: ITag[] | undefined | null;
  currentArticleSysId: string;
}

// TODO: func deprecated => no need update with IMarket, but delete func
export const getRelatedArticles = async (args: IGetRelatedArticleArgs) => {
  const { isPreviewMode, currentArticleTags, currentArticleSysId } = args;

  const firstTag = currentArticleTags?.[0];

  if (!firstTag) return null;

  const thumbnailsWithFirstTag: ThumbnailProps[] =
    (await getThumbnailsByTag({
      isPreviewMode,
      slug: firstTag.slug,
      type: firstTag.type,
    })) || [];

  const allMatchedTagThumbnails: ThumbnailProps[] = thumbnailsWithFirstTag?.filter((article: ThumbnailProps) =>
    currentArticleTags
      ?.slice(1)
      ?.every((currentTag: ITag) => article?.tags?.some((tag: ITag) => tag?.sys?.id === currentTag?.sys?.id)),
  );

  const sortedMatchedThumbnails = sortByDate(allMatchedTagThumbnails) || [];

  const relatedThumbnails: ThumbnailProps[] = [...sortedMatchedThumbnails, ...thumbnailsWithFirstTag];

  const uniqueRelatedThumbnailsBySysId = relatedThumbnails?.reduce(
    (result: ThumbnailProps[], current: ThumbnailProps) => {
      const isDuplicate = result?.some((article: ThumbnailProps) => article.sys.id === current.sys.id);
      const isCurrentArticle = current.sys.id === currentArticleSysId; // Except current post

      return isDuplicate || isCurrentArticle ? result : [...result, current];
    },
    [],
  );

  return uniqueRelatedThumbnailsBySysId;
};

export const getAllArticlesRssInfo = async (): Promise<ISimplifiedArticleProps[] | null> => {
  const ARTICLES_RSS_LIMIT = 1000;
  const query = `
    {
      articleCollection(
        preview: false
        where: {
          articleType: "${ArticleTypeName.insights}"
        }
        limit: ${ARTICLES_RSS_LIMIT}
      ) {
        items {
          sys {
            id
          }
          title
          slug
          date
          image {
            size
            contentType
            url(transform: {
              width: 696,
            })
          }
          content {
            json
          }
          seo {
            description
          }
        }
      }
    }
  `;

  const response = await callContentful(query, false, '/rss', 'article.fetchers.getAllArticlesRssInfo');

  return response.data?.articleCollection?.items?.filter(Boolean) || null;
};
